import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { format, getDate, isToday } from 'date-fns';
import type { LendableDeskInfo, DeskLendingDateStatus } from 'garaje/services/desk-lending';
import { LENDING_STATUS } from 'garaje/utils/enums';

interface DeskLendingCardArgs {
  desk: LendableDeskInfo;
  date: Date;
  lendingStatus: DeskLendingDateStatus | undefined;
  onLend: () => Promise<void>;
  onReclaim: () => Promise<void>;
  onSchedule: () => Promise<void>;
  onUnlend: () => Promise<void>;
  isScheduled: boolean;
  showDate: boolean;
}

export default class DeskLendingCardComponent extends Component<DeskLendingCardArgs> {
  @tracked isLending = false;
  @tracked isReclaiming = false;
  @tracked isScheduling = false;
  @tracked isUnlending = false;
  @tracked reclaimModalIsOpen = false;

  get status(): string {
    return this.args.lendingStatus?.lendingStatus ?? LENDING_STATUS.NOT_LENT;
  }

  get isLentAvailable(): boolean {
    return this.status === LENDING_STATUS.LENT_AVAILABLE;
  }

  get isLentBooked(): boolean {
    return this.status === LENDING_STATUS.LENT_BOOKED;
  }

  get isNotLent(): boolean {
    return this.status === LENDING_STATUS.NOT_LENT;
  }

  get bookerName(): string {
    return this.args.lendingStatus?.reservation?.bookedByName ?? 'Someone';
  }

  get canReclaim(): boolean {
    return !isToday(this.args.date);
  }

  get dayOfWeek(): string {
    return format(this.args.date, 'eee');
  }

  get dateNumber(): number {
    return getDate(this.args.date);
  }

  get deskLabel(): string {
    return `${this.args.desk.deskName}, ${this.args.desk.floorName}`;
  }

  get formattedReclaimDate(): string {
    return format(this.args.date, 'MMM d, yyyy');
  }

  @action
  async handleLend(): Promise<void> {
    this.isLending = true;
    try {
      await this.args.onLend();
    } finally {
      this.isLending = false;
    }
  }

  @action
  async handleReclaim(): Promise<void> {
    this.isReclaiming = true;
    try {
      await this.args.onReclaim();
    } finally {
      this.isReclaiming = false;
      this.reclaimModalIsOpen = false;
    }
  }

  @action
  async handleSchedule(): Promise<void> {
    this.isScheduling = true;
    try {
      await this.args.onSchedule();
    } finally {
      this.isScheduling = false;
    }
  }

  @action
  async handleUnlend(): Promise<void> {
    this.isUnlending = true;
    try {
      await this.args.onUnlend();
    } finally {
      this.isUnlending = false;
    }
  }
}
